import { Button, EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { PenLine } from "lucide-react";
import { useAccess } from "~/core/access/index";
import { api } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { settings } from "~/core/paths";
import { keys, read } from "~/core/query";
import { Async } from "~/core/ui/states";
import { Drill, DrillLink } from "~/modules/analytics/components/detail";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { useWriteAbout } from "~/modules/analytics/lib/write";

/**
 * What assistants get wrong.
 *
 * This is close to free for us and expensive for anyone else: the ground truth
 * is the entity's own sourced datapoints, which a monitoring tool without a
 * directory behind it simply does not have.
 */
export function FactCheckPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const { write, busy } = useWriteAbout(entityId, filters.locale);
	const state = useQuery({
		queryKey: [
			...keys.entity(entityId),
			"factcheck",
			{ range: filters.range, engine: filters.engine, locale: filters.locale, topics: filters.topics },
		],
		queryFn: () => api.factcheck(entityId, filters),
		...read,
	});

	return (
		<StageShell
			entityId={entityId}
			reading="factcheck"
			lede={t("factcheck.lede")}
			filters={filters}
			setFilters={setFilters}
		>
			<Async
				state={state}
				empty={<EmptyState size="compact" title={t("factcheck.emptyTitle")} description={t("factcheck.empty")} />}
			>
				{(claims) => (
					<div className="table-scroll">
						<Table variant="resource">
							<TableHeader>
								<TableRow>
									<TableHead className="wrap">{t("factcheck.claim")}</TableHead>
									<TableHead className="wrap">{t("factcheck.truth")}</TableHead>
									<TableHead>{t("factcheck.severity")}</TableHead>
									<TableHead>{t("table.engine")}</TableHead>
									<TableHead>{t("table.date")}</TableHead>
									<TableHead />
								</TableRow>
							</TableHeader>
							<TableBody>
								{claims.map((claim) => (
									<TableRow key={claim.id}>
										<TableCell className="wrap">
											{/* The claim opens the answer it was read out of. A
											    sentence an assistant got wrong is arguable only
											    next to the paragraph it appeared in. */}
											<Drill view={{ kind: "answer", label: claim.question, sampleId: claim.sample_id }}>
												{claim.claim}
											</Drill>
											<div className="faint">{claim.question}</div>
										</TableCell>
										<TableCell className="wrap">
											{claim.truth}
											{claim.datapoint_key ? <div className="faint mono">{claim.datapoint_key}</div> : null}
										</TableCell>
										<TableCell>
											<span className={severityClass(claim.severity)}>{t(`factcheck.severity.${claim.severity}`)}</span>
										</TableCell>
										<TableCell>{claim.engine}</TableCell>
										<TableCell className="mono">{fmt.date(locale, claim.created_at)}</TableCell>
										<TableCell className="num">
											{/* Two ways a wrong answer gets fixed: the fact the check
											    compared against is itself wrong or missing, or it is
											    right and nothing the engines read says so yet. */}
											<div className="row-actions justify-end">
												<DrillLink to={settings(entityId, "facts")}>{t("factcheck.checkFact")}</DrillLink>
												<Button
													variant="default"
													type="button"
													disabled={!editor || busy !== null}
													onClick={() => write(claim.question)}
												>
													<PenLine aria-hidden />
													{t("factcheck.write")}
												</Button>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</Async>
		</StageShell>
	);
}

function severityClass(severity: string): string {
	if (severity === "high") return "pill pill-danger";
	if (severity === "medium") return "pill pill-warning";
	return "pill";
}
